import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  ArrowLeft, Star, MessageCircle, UserPlus, UserCheck, 
  Users, Clock, Phone, MapPin, Calendar, Globe,
  Share2, MoreVertical, Heart, MessageSquare, Bookmark,
  ExternalLink, Building2, User, CheckCircle, XCircle,
  Send, X, Check, Copy, Image as ImageIcon, AlertCircle,
  Repeat, ChevronDown, ChevronUp, Grid, List, Flag, BellOff, Bell
} from "lucide-react";
import io from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import FollowButton from "./FollowButton";
import LevelBadge from "./LevelBadge.jsx";
import ProBadge from "./ProBadge.jsx";
import { API_BASE_URL, SOCKET_URL, resolveMediaUrl } from "../utils/network.js";
import { useSmartBack } from "../utils/useSmartBack.js";
import { localizeMessage } from '../utils/toast.js';
import './UserPublicProfile.css';

const getToken = () => localStorage.getItem("token");

const defaultAvatar = "/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png";
const defaultCover = "/nkarner/default-cover.jpg";

const formatDate = (value, t) => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return t('userPublicProfile.justNow');
  if (minutes < 60) return t('userPublicProfile.minutesAgo', { n: minutes });
  if (hours < 24) return t('userPublicProfile.hoursAgo', { n: hours });
  if (days < 7) return t('userPublicProfile.daysAgo', { n: days });
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const formatFullDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const StarRow = ({ rating, size = 13 }) => {
  const r = Math.round(Number(rating) || 0);
  return (
    <span className="upp-star-row" aria-label={`${r} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          fill={i <= r ? "var(--amber-600)" : "none"}
          color={i <= r ? "var(--amber-600)" : "var(--ink-400)"}
        />
      ))}
    </span>
  );
};

const ImageGallery = ({ images, getImageUrl, onImageClick }) => {
  const { t } = useTranslation();
  if (!images || images.length === 0) return null;
  const getGridClass = () => {
    switch(images.length) {
      case 1: return 'grid-one';
      case 2: return 'grid-two';
      case 3: return 'grid-three';
      default: return 'grid-more';
    }
  };
  return (
    <div className={`image-gallery ${getGridClass()}`}>
      {images.slice(0, 4).map((image, index) => (
        <div key={index} className="gallery-item" onClick={() => onImageClick(index)}>
          <img src={getImageUrl(image)} alt={t('userPublicProfile.postImageAlt', { n: index + 1 })} className="gallery-image" />
          {index === 3 && images.length > 4 && (
            <div className="gallery-overlay"><span>+{images.length - 4}</span></div>
          )}
        </div>
      ))}
    </div>
  );
};

function UserPublicProfile() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack('/feed');
  const socketRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followStatus, setFollowStatus] = useState({
    isFollowing: false, isFollower: false, hasSentRequest: false,
    requestStatus: null, canMessage: false
  });
  const [userStats, setUserStats] = useState({ followers: 0, following: 0, posts: 0 });
  const [files, setFiles] = useState({ reports: [], portfolio: [] });
  const [certificates, setCertificates] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingFollow, setLoadingFollow] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [muting, setMuting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReasonId, setReportReasonId] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [isCommenting, setIsCommenting] = useState({});
  const [isLiking, setIsLiking] = useState({});
  const [isSaving, setIsSaving] = useState({});
  const [avatarError, setAvatarError] = useState(false);
  const [coverError, setCoverError] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const commentInputRef = useRef(null);

  useEffect(() => {
    const token = getToken();
    if (!token || !userId) return;
    socketRef.current = io(SOCKET_URL, { query: { token, userId }, transports: ['websocket', 'polling'] });
    socketRef.current.on('followStatusChanged', (data) => {
      if (data.targetUserId === userId) { loadFollowStatus(); loadUserStats(); }
    });
    socketRef.current.on('profileUpdated', (updatedProfile) => {
      if (updatedProfile._id === userId) setProfile(prev => ({ ...prev, ...updatedProfile }));
    });
    socketRef.current.on('newPost', (newPost) => {
      if (newPost.author?._id === userId) setPosts(prev => [newPost, ...prev]);
    });
    socketRef.current.on('postUpdated', (updatedPost) => {
      setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    });
    socketRef.current.on('postDeleted', (deletedPostId) => {
      setPosts(prev => prev.filter(p => p._id !== deletedPostId));
    });
    return () => { if (socketRef.current) socketRef.current.disconnect(); };
  }, [userId]);

  useEffect(() => {
    const token = getToken();
    if (!token) { setError(t('userPublicProfile.loginRequired')); setLoadingProfile(false); setLoadingPosts(false); setLoadingFollow(false); return; }
    fetchCurrentUser(token);
    fetchProfile(token);
    fetchPosts(token);
    loadUserStats();
  }, [userId]);

  useEffect(() => {
    if (!currentUser || !userId) return;
    loadFollowStatus();
  }, [currentUser, userId]);

  useEffect(() => { setAvatarError(false); setCoverError(false); }, [userId]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/profile`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (response.ok) { const data = await response.json(); if (data.success) setCurrentUser(data.user); }
    } catch (error) { console.error("Error fetching current user:", error); }
  };

  const isBlocked = (currentUser?.blockedUsers || []).some((id) => String(id?._id || id) === String(userId));
  const isMuted = (currentUser?.mutedUsers || []).some((id) => String(id?._id || id) === String(userId));

  const handleToggleMute = async () => {
    const token = getToken();
    if (!token) return;
    const action = isMuted ? 'unmute' : 'mute';
    try {
      setMuting(true);
      const response = await fetch(`${API_BASE_URL}/users/${userId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setCurrentUser((prev) => ({
          ...prev,
          mutedUsers: isMuted
            ? (prev.mutedUsers || []).filter((id) => String(id?._id || id) !== String(userId))
            : [...(prev.mutedUsers || []), userId],
        }));
        setSuccessMessage(isMuted ? t('userPublicProfile.userUnmuted') : t('userPublicProfile.userMuted'));
      } else {
        setError(data.error || (isMuted ? t('userPublicProfile.failedToUnmuteUser') : t('userPublicProfile.failedToMuteUser')));
      }
    } catch (err) {
      setError(isMuted ? t('userPublicProfile.failedToUnmuteUser') : t('userPublicProfile.failedToMuteUser'));
    } finally {
      setMuting(false);
      setShowMoreMenu(false);
    }
  };

  const handleToggleBlock = async () => {
    const token = getToken();
    if (!token) return;
    const action = isBlocked ? 'unblock' : 'block';
    if (!isBlocked && !window.confirm(t('userPublicProfile.confirmBlock', { name: displayName }))) return;
    try {
      setBlocking(true);
      const response = await fetch(`${API_BASE_URL}/users/${userId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setCurrentUser((prev) => ({
          ...prev,
          blockedUsers: isBlocked
            ? (prev.blockedUsers || []).filter((id) => String(id?._id || id) !== String(userId))
            : [...(prev.blockedUsers || []), userId],
        }));
        setSuccessMessage(isBlocked ? t('userPublicProfile.userUnblocked') : t('userPublicProfile.userBlocked'));
      } else {
        setError(data.error || (isBlocked ? t('userPublicProfile.failedToUnblockUser') : t('userPublicProfile.failedToBlockUser')));
      }
    } catch (err) {
      setError(isBlocked ? t('userPublicProfile.failedToUnblockUser') : t('userPublicProfile.failedToBlockUser'));
    } finally {
      setBlocking(false);
      setShowMoreMenu(false);
    }
  };

  const REPORT_REASONS = [
    { id: 'spam', label: t('userPublicProfile.reportReasons.spam') },
    { id: 'inappropriate', label: t('userPublicProfile.reportReasons.inappropriate') },
    { id: 'harassment', label: t('userPublicProfile.reportReasons.harassment') },
    { id: 'hate_speech', label: t('userPublicProfile.reportReasons.hateSpeech') },
    { id: 'misinformation', label: t('userPublicProfile.reportReasons.misinformation') },
    { id: 'other', label: t('userPublicProfile.reportReasons.other') },
  ];

  const handleSubmitReport = async () => {
    const token = getToken();
    if (!token || !reportReasonId) return;
    try {
      setReporting(true);
      const response = await fetch(`${API_BASE_URL}/reports`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ type: 'user', targetId: userId, reason: reportReasonId, details: reportDetails }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage(t('userPublicProfile.reportSubmitted'));
        setShowReportModal(false);
        setReportReasonId('');
        setReportDetails('');
      } else {
        setError(data.error || t('userPublicProfile.failedSubmitReport'));
      }
    } catch {
      setError(t('userPublicProfile.failedSubmitReport'));
    } finally {
      setReporting(false);
    }
  };

  const fetchProfile = async (token) => {
    try {
      setLoadingProfile(true); setError(null);
      const response = await fetch(`${API_BASE_URL}/users/${userId}/profile`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || t('userPublicProfile.failedLoadProfile'));
      setProfile(data.user);
      setFiles({ reports: Array.isArray(data.user.reports) ? data.user.reports : [], portfolio: Array.isArray(data.user.portfolio) ? data.user.portfolio : [] });
      setCertificates(Array.isArray(data.user.certificates) ? data.user.certificates : []);
    } catch (err) { setError(err.message); } finally { setLoadingProfile(false); }
  };

  const fetchPosts = async (token) => {
    try {
      setLoadingPosts(true);
      const response = await fetch(`${API_BASE_URL}/users/${userId}/posts`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || t('userPublicProfile.failedLoadPosts'));
      setPosts(data.posts || []);
      setUserStats(prev => ({ ...prev, posts: data.posts?.length || 0 }));
    } catch (err) { setError((prev) => prev || err.message); } finally { setLoadingPosts(false); }
  };

  const loadFollowStatus = async () => {
    try {
      setLoadingFollow(true);
      const token = getToken();
      if (!token || !currentUser) return;
      const response = await fetch(`${API_BASE_URL}/users/${userId}/follow-status`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setFollowStatus({ isFollowing: data.isFollowing, isFollower: data.isFollowedBy, hasSentRequest: data.hasRequested, requestStatus: data.requestStatus, canMessage: data.canMessage || (data.isFollowing && data.isFollowedBy) });
      }
    } catch (error) { console.error("Error loading follow status:", error); } finally { setLoadingFollow(false); }
  };

  const loadUserStats = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const [followersListRes, followingListRes] = await Promise.all([
        fetch(`${API_BASE_URL}/users/${userId}/followers`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_BASE_URL}/users/${userId}/following`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => null)
      ]);
      if (followersListRes?.ok) { const data = await followersListRes.json(); if (data.success) { setFollowers(data.followers || []); setUserStats(prev => ({ ...prev, followers: data.followers?.length || 0 })); } }
      if (followingListRes?.ok) { const data = await followingListRes.json(); if (data.success) { setFollowing(data.following || []); setUserStats(prev => ({ ...prev, following: data.following?.length || 0 })); } }
    } catch (error) { console.error("Error loading user stats:", error); }
  };

  const handleFollowStatusChange = () => { loadFollowStatus(); loadUserStats(); };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      setSendingMessage(true);
      const token = getToken();
      if (!token || !followStatus.canMessage) { setError(t('userPublicProfile.needMutualFollowMessage')); setTimeout(() => setError(null), 3000); return; }
      const convResponse = await fetch(`${API_BASE_URL}/conversations`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ participantIds: [userId], isGroup: false }) });
      if (convResponse.ok) {
        const convData = await convResponse.json();
        const msgResponse = await fetch(`${API_BASE_URL}/conversations/${convData.conversation._id}/messages`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ content: newMessage }) });
        if (msgResponse.ok) { setSuccessMessage(t('userPublicProfile.messageSent')); setNewMessage(''); navigate(`/messages/dm/${convData.conversation._id}`); }
      }
    } catch (error) { setError(t('userPublicProfile.failedSendMessage')); } finally { setSendingMessage(false); }
  };

  const navigateToMessages = async () => {
    try {
      const token = getToken();
      if (!token || !followStatus.canMessage) { setError(t('userPublicProfile.needMutualFollowMessage')); setTimeout(() => setError(null), 3000); return; }
      const response = await fetch(`${API_BASE_URL}/conversations`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ participantIds: [userId], isGroup: false }) });
      if (response.ok) { const data = await response.json(); navigate(`/messages/dm/${data.conversation._id}`); }
    } catch (error) { setError(t('userPublicProfile.failedStartConversation')); }
  };

  const handleShareProfile = async () => {
    try {
      const profileUrl = `${window.location.origin}/profile/${userId}`;
      await navigator.clipboard.writeText(profileUrl);
      setCopiedLink(true); setSuccessMessage(t('userPublicProfile.profileLinkCopied')); setShowShareMenu(false);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) { setError(t('userPublicProfile.failedShareProfile')); }
  };

  const handleLikePost = async (postId) => {
    if (isLiking[postId]) return;
    setIsLiking(prev => ({ ...prev, [postId]: true }));
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/like`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (response.ok) { const data = await response.json(); setPosts(posts.map(p => p._id === postId ? data.post : p)); }
    } catch (error) {} finally { setIsLiking(prev => ({ ...prev, [postId]: false })); }
  };

  const isPostLiked = (post) => {
    if (!currentUser || !post.likes) return false;
    return post.likes.some(likeId => likeId === currentUser._id || (typeof likeId === 'object' && likeId._id === currentUser._id));
  };

  const handleSavePost = async (postId) => {
    if (isSaving[postId]) return;
    setIsSaving(prev => ({ ...prev, [postId]: true }));
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/save`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (response.ok) { const data = await response.json(); setPosts(posts.map(p => p._id === postId ? data.post : p)); }
    } catch (error) {} finally { setIsSaving(prev => ({ ...prev, [postId]: false })); }
  };

  const isPostSaved = (post) => {
    if (!currentUser || !post.saves) return false;
    return post.saves.some(saveId => saveId === currentUser._id || (typeof saveId === 'object' && saveId._id === currentUser._id));
  };

  const handleComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim() || isCommenting[postId]) return;
    setIsCommenting(prev => ({ ...prev, [postId]: true }));
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/comments`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ content }) });
      if (response.ok) { const data = await response.json(); setPosts(posts.map(p => p._id === postId ? data.post : p)); setCommentInputs(prev => ({ ...prev, [postId]: '' })); }
    } catch (error) {} finally { setIsCommenting(prev => ({ ...prev, [postId]: false })); }
  };

  const getImageUrl = (imagePath, type = 'avatar') => {
    if (!imagePath) return type === 'cover' ? defaultCover : defaultAvatar;
    return resolveMediaUrl(imagePath, type === 'cover' ? defaultCover : defaultAvatar);
  };

  const resolveFileUrl = (url) => {
    if (!url) return '#';
    return resolveMediaUrl(url, url);
  };

  const getFileExtension = (file) => {
    const raw = String(file?.extension || file?.name || '').toLowerCase();
    const parts = raw.split('.');
    return parts.length > 1 ? parts.pop() : raw;
  };

  const getFileKind = (file) => {
    const type = String(file?.type || '').toLowerCase();
    const ext = getFileExtension(file);
    if (type.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (type.includes('pdf') || ext === 'pdf') return 'pdf';
    return 'file';
  };

  const isPreviewableFile = (file) => {
    const kind = getFileKind(file);
    return kind === 'pdf' || kind === 'image';
  };

  const handlePreviewFile = (file) => {
    const url = resolveFileUrl(file?.url);
    if (!url || url === '#') return;
    if (isPreviewableFile(file)) {
      setPreviewFile({ ...file, resolvedUrl: url });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadFile = (file) => {
    const url = resolveFileUrl(file?.url);
    if (!url || url === '#') return;
    const link = document.createElement('a');
    link.href = url;
    link.download = file?.name || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseInt(String(n).replace(/\D/g, ''), 10);
    if (!Number.isFinite(num) || num < 0) return String(n);
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAvatarSrc = () => {
    if (!profile || avatarError) return defaultAvatar;
    return getImageUrl(profile.profileImage, 'avatar');
  };

  const getCoverSrc = () => {
    if (!profile || coverError) return null;
    if (!profile.coverImage || profile.coverImage.includes('default-cover')) return null;
    return getImageUrl(profile.coverImage, 'cover');
  };

  const getUserAvatarSrc = (user) => {
    if (!user || !user.profileImage) return defaultAvatar;
    return getImageUrl(user.profileImage, 'avatar');
  };

  const navigateToProfile = (targetUserId) => {
    if (targetUserId === currentUser?._id) navigate('/dashboard');
    else navigate(`/profile/${targetUserId}`);
  };

  if (loadingProfile) {
    return (
      <div className="upp-root upp-loading-screen">
        <div className="upp-loading-spinner"></div>
        <p className="upp-loading-text">{t('userPublicProfile.loadingProfile')}</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="upp-root upp-error-screen">
        <div className="upp-error-card">
          <AlertCircle size={48} color="var(--upp-danger)" className="upp-error-icon" />
          <p className="upp-error-text">{error || t('userPublicProfile.userNotFound')}</p>
          <button onClick={goBack} className="upp-error-btn">
            <ArrowLeft size={16} /> {t('userPublicProfile.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const isOrganization = profile.userType === 'organizer';
  const isVerifiedOrganization = isOrganization && profile.isVerified;
  const displayName = isOrganization ? profile.organizationName || profile.name : [profile.name, profile.surname].filter(Boolean).join(" ") || profile.username;
  const interestKeyMap = {
    "Environment": "environment", "Education": "education", "Technology": "technology", "Health": "health",
    "Arts & Culture": "artsCulture", "Community Development": "communityDevelopment", "Human Rights": "humanRights",
    "Social Inclusion": "socialInclusion", "International Exchanges": "internationalExchanges", "Entrepreneurship": "entrepreneurship",
  };
  const trInterest = (name) => t(`userPublicProfile.interests.${interestKeyMap[name] || name}`, name);

  const titleText = isOrganization ? (profile.orgType?.[0] || t('userPublicProfile.organization')) : t('userPublicProfile.communityMember');
  const isCurrentUserProfile = currentUser && currentUser._id === userId;
  const isMutualFollow = followStatus.isFollowing && followStatus.isFollower;

  const tabs = [
    { id: 'posts', label: t('userPublicProfile.postsLabel') },
    { id: 'about', label: t('userPublicProfile.aboutLabel') },
    { id: 'report', label: t('userPublicProfile.reportsTabLabel') },
    { id: 'portfolio', label: t('userPublicProfile.portfolioLabel') },
    { id: 'certificates', label: t('userPublicProfile.certificatesLabel') },
    ...(isOrganization ? [{ id: 'reviews', label: 'Reviews' }] : []),
    { id: 'followers', label: t('userPublicProfile.followersLabel') },
    { id: 'following', label: t('userPublicProfile.followingLabel') },
  ];

  return (
    <>
      <div className="upp-root">
        
        {/* Back */}
        <div className="upp-back-bar">
          <button className="upp-back-btn" onClick={goBack}>
            <ArrowLeft size={16} />
            {t('userPublicProfile.back')}
          </button>
        </div>

        {/* Cover */}
        <div className="upp-cover">
          {getCoverSrc() ? (
            <img src={getCoverSrc()} alt={t('userPublicProfile.coverAlt')} className="upp-cover-img" onError={() => setCoverError(true)} />
          ) : (
            <div className={`upp-cover-gradient${profile?.customProfileDesign ? ` theme-${profile.customProfileDesign}` : ''}`} />
          )}
          <div className="upp-cover-fade" />
        </div>

        {/* Profile Card */}
        <div
          className={`upp-profile-card${profile?.customProfileDesign ? ` theme-${profile.customProfileDesign}` : ''}`}
          style={{ '--upp-accent': profile?.profileColor || '#32614d' }}
        >
          <div className={`upp-avatar-wrap${profile?.customFrame ? ` avatar-frame-${profile.customFrame}` : ''}`}>
            <img
              src={getAvatarSrc()}
              alt={displayName}
              className="upp-avatar"
              onError={() => setAvatarError(true)}
            />
            {avatarError && (
              <div className="upp-avatar-fallback">
                <span>{displayName?.charAt(0)?.toUpperCase() || '?'}</span>
              </div>
            )}
            {profile?.isVerified && (
              <div className="upp-verified-badge" title={t('userPublicProfile.verified')}>
                <Star size={12} fill="var(--surface-white)" color="var(--surface-white)" />
              </div>
            )}
            <LevelBadge level={profile?.level} size="sm" />
            {profile?.isPro && <ProBadge size="sm" />}
          </div>

          <div className="upp-profile-body">
            <div className="upp-name-row">
              <div>
                <h1 className="upp-display-name">{displayName}</h1>
                <div className="upp-badges-row">
                  <span className="upp-badge upp-badge-type">
                    {isOrganization ? <Building2 size={12} /> : <User size={12} />}
                    {isOrganization ? (isVerifiedOrganization ? t('userPublicProfile.verifiedOrg') : t('userPublicProfile.organization')) : t('userPublicProfile.communityMember')}
                  </span>
                  {profile.isApproved === 'approved' && (
                    <span className="upp-badge upp-badge-approved">
                      <CheckCircle size={12} /> {t('userPublicProfile.approved')}
                    </span>
                  )}
                  <LevelBadge level={profile.level} />
                  {profile.isPro && <ProBadge />}
                </div>
                {profile.title && <p className="upp-title-text">{profile.title}</p>}
              </div>

              <div className="upp-action-area">
                {!isCurrentUserProfile && (
                  <>
                    <FollowButton userId={userId} username={displayName} profileImage={profile.profileImage} onFollowChange={handleFollowStatusChange} />
                    {isMutualFollow && (
                      <button className="upp-btn upp-btn-msg" onClick={navigateToMessages}>
                        <MessageCircle size={16} /> {t('userPublicProfile.message')}
                      </button>
                    )}
                  </>
                )}
                <div className="upp-menu-wrap">
                  <button className="upp-btn upp-btn-share" onClick={() => setShowShareMenu(v => !v)}>
                    <Share2 size={16} />
                  </button>
                  {showShareMenu && (
                    <div className="upp-share-menu">
                      <button className="upp-share-item" onClick={handleShareProfile}>
                        {copiedLink ? <><Check size={14} /> {t('userPublicProfile.copied')}</> : <><Copy size={14} /> {t('userPublicProfile.copyLink')}</>}
                      </button>
                      <button className="upp-share-item" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}>
                        <ExternalLink size={14} /> {t('userPublicProfile.shareOnFacebook')}
                      </button>
                    </div>
                  )}
                </div>
                {!isCurrentUserProfile && (
                  <div className="upp-menu-wrap">
                    <button className="upp-btn upp-btn-share" onClick={() => setShowMoreMenu(v => !v)} title={t('userPublicProfile.moreOptions')}>
                      <MoreVertical size={16} />
                    </button>
                    {showMoreMenu && (
                      <div className="upp-share-menu">
                        <button className="upp-share-item" onClick={handleToggleMute} disabled={muting}>
                          {muting ? t('userPublicProfile.pleaseWait') : isMuted ? <><Bell size={14} /> {t('userPublicProfile.unmute')}</> : <><BellOff size={14} /> {t('userPublicProfile.mutePosts')}</>}
                        </button>
                        <button className={`upp-share-item${isBlocked ? '' : ' upp-share-item-danger'}`} onClick={handleToggleBlock} disabled={blocking}>
                          {blocking ? t('userPublicProfile.pleaseWait') : isBlocked ? <><UserCheck size={14} /> {t('userPublicProfile.unblock')}</> : <><XCircle size={14} /> {t('userPublicProfile.blockUser')}</>}
                        </button>
                        <button className="upp-share-item upp-share-item-danger" onClick={() => { setShowMoreMenu(false); setShowReportModal(true); }}>
                          <Flag size={14} /> {t('userPublicProfile.reportUser')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="upp-stats-row">
              <div className="upp-stat" onClick={() => setActiveTab('posts')}>
                <span className="upp-stat-num">{userStats.posts}</span>
                <span className="upp-stat-lbl">{t('userPublicProfile.postsLabel')}</span>
              </div>
              <div className="upp-stat-divider" />
              <div className="upp-stat" onClick={() => setActiveTab('followers')}>
                <span className="upp-stat-num">{userStats.followers}</span>
                <span className="upp-stat-lbl">{t('userPublicProfile.followersLabel')}</span>
              </div>
              <div className="upp-stat-divider" />
              <div className="upp-stat" onClick={() => setActiveTab('following')}>
                <span className="upp-stat-num">{userStats.following}</span>
                <span className="upp-stat-lbl">{t('userPublicProfile.followingLabel')}</span>
              </div>
            </div>

            {/* Meta info */}
            <div className="upp-meta-row">
              {profile.location && <span className="upp-meta-item"><MapPin size={14} />{profile.location}</span>}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="upp-meta-item upp-meta-link">
                  <Globe size={14} />{profile.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              <span className="upp-meta-item"><Calendar size={14} />{t('userPublicProfile.joined', { date: formatFullDate(profile.createdAt) })}</span>
            </div>

            {profile.bio && <p className="upp-bio">{profile.bio}</p>}

            {/* Quick message */}
            {isMutualFollow && !isCurrentUserProfile && (
              <div className="upp-quick-msg">
                <input
                  type="text"
                  placeholder={t('userPublicProfile.quickMessagePlaceholder')}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="upp-msg-input"
                />
                <button className="upp-msg-send" onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}>
                  {sendingMessage ? '…' : <Send size={15} />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Toast messages */}
        {error && <div className="upp-toast upp-toast-error"><AlertCircle size={16} />{localizeMessage(error)}</div>}
        {successMessage && <div className="upp-toast upp-toast-success"><CheckCircle size={16} />{successMessage}</div>}

        {/* Tabs */}
        <div className="upp-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`upp-tab${activeTab === tab.id ? ' upp-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="upp-content">

          {/* POSTS */}
          {activeTab === 'posts' && (
            <div className="upp-posts-section">
              {loadingPosts ? (
                <div className="upp-loading-center">
                  <div className="upp-spinner" />
                </div>
              ) : posts.length === 0 ? (
                <div className="upp-empty">
                  <ImageIcon size={40} className="upp-empty-icon" />
                  <p>{t('userPublicProfile.noPostsYet')}</p>
                </div>
              ) : (
                <div className="upp-posts-grid">
                  {posts.map(post => (
                    <div
                      key={post._id}
                      className="upp-grid-item"
                      onClick={() => { setSelectedPost(post); setShowPostModal(true); }}
                    >
                      {post.images && post.images.length > 0 ? (
                        <>
                          <img
                            src={getImageUrl(post.images[0])}
                            alt={t('userPublicProfile.postAlt')}
                            className="upp-grid-img"
                            onError={(e) => { e.target.src = defaultAvatar; }}
                          />
                          {post.images.length > 1 && (
                            <div className="upp-multi-badge">
                              <ImageIcon size={12} /> {post.images.length}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="upp-grid-text">
                          <p>{post.content?.substring(0, 120)}{post.content?.length > 120 ? '…' : ''}</p>
                        </div>
                      )}
                      <div className="upp-grid-hover">
                        <span><Heart size={16} fill="white" color="white" /> {post.likes?.length || 0}</span>
                        <span><MessageCircle size={16} color="white" /> {post.comments?.length || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ABOUT */}
          {activeTab === 'about' && (
            <div className="upp-about">
              {profile.bio && (
                <div className="upp-info-card">
                  <h3 className="upp-info-title">{t('userPublicProfile.aboutLabel')}</h3>
                  <p className="upp-info-text">{profile.bio}</p>
                </div>
              )}
              <div className="upp-info-grid">
                {profile.name && (
                  <div className="upp-info-card">
                    <div className="upp-info-label"><User size={16} color="var(--green-500)" /> {t('userPublicProfile.nameLabel')}</div>
                    <p className="upp-info-text">{profile.name} {profile.surname || ''}</p>
                  </div>
                )}
                {profile.phone && (
                  <div className="upp-info-card">
                    <div className="upp-info-label"><Phone size={16} color="var(--green-500)" /> {t('userPublicProfile.phoneLabel')}</div>
                    <p className="upp-info-text">{profile.phone}</p>
                  </div>
                )}
                {profile.location && (
                  <div className="upp-info-card">
                    <div className="upp-info-label"><MapPin size={16} color="var(--green-500)" /> {t('userPublicProfile.locationLabel')}</div>
                    <p className="upp-info-text">{profile.location}</p>
                  </div>
                )}
                {profile.interests && profile.interests.length > 0 && (
                  <div className="upp-info-card upp-info-card-wide">
                    <div className="upp-info-label"><Star size={16} color="var(--amber-600)" /> {t('userPublicProfile.interestsLabel')}</div>
                    <div className="upp-tags">
                      {profile.interests.map((interest, i) => (
                        <span key={i} className="upp-tag">{trInterest(interest)}</span>
                      ))}
                    </div>
                  </div>
                )}
                {isOrganization && profile.mission && (
                  <div className="upp-info-card upp-info-card-wide">
                    <div className="upp-info-label"><Building2 size={16} color="var(--green-500)" /> {t('userPublicProfile.missionLabel')}</div>
                    <p className="upp-info-text">{profile.mission}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* REPORTS */}
          {activeTab === 'report' && (
            <div className="upp-files-section">
              <h3 className="upp-section-title">{t('userPublicProfile.reportsTitle')}</h3>
              {files.reports.length === 0 ? (
                <div className="upp-empty"><p>{t('userPublicProfile.noReportsYet')}</p></div>
              ) : (
                <div className="upp-files-list">
                  {files.reports.map((file, i) => (
                    <div key={file.id || file._id || i} className="upp-file-item">
                      <div>
                        <p className="upp-file-name">{file.name}</p>
                        <p className="upp-file-meta">{formatFileSize(file.size)} · {formatDate(file.uploadDate, t)}</p>
                      </div>
                      {file.url && (
                        <div className="upp-file-actions">
                          <button type="button" onClick={() => handlePreviewFile(file)} className="upp-file-link upp-file-link-secondary">{t('userPublicProfile.view')}</button>
                          <button type="button" onClick={() => handleDownloadFile(file)} className="upp-file-link">{t('userPublicProfile.download')}</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PORTFOLIO */}
          {activeTab === 'portfolio' && (
            <div className="upp-files-section">
              <h3 className="upp-section-title">{t('userPublicProfile.portfolioLabel')}</h3>
              {files.portfolio.length === 0 ? (
                <div className="upp-empty"><p>{t('userPublicProfile.noPortfolioYet')}</p></div>
              ) : (
                <div className="upp-files-list">
                  {files.portfolio.map((file, i) => (
                    <div key={file.id || file._id || i} className="upp-file-item">
                      <div>
                        <p className="upp-file-name">{file.name}</p>
                        <p className="upp-file-meta">{formatFileSize(file.size)} · {formatDate(file.uploadDate, t)}</p>
                      </div>
                      {file.url && (
                        <div className="upp-file-actions">
                          <button type="button" onClick={() => handlePreviewFile(file)} className="upp-file-link upp-file-link-secondary">{t('userPublicProfile.view')}</button>
                          <button type="button" onClick={() => handleDownloadFile(file)} className="upp-file-link">{t('userPublicProfile.download')}</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CERTIFICATES */}
          {activeTab === 'certificates' && (
            <div className="upp-files-section">
              <h3 className="upp-section-title">{t('userPublicProfile.certificatesLabel')}</h3>
              {certificates.length === 0 ? (
                <div className="upp-empty"><p>{t('userPublicProfile.noCertificatesYet')}</p></div>
              ) : (
                <div className="upp-files-list">
                  {certificates.map((cert) => (
                    <div key={cert.id} className="upp-file-item">
                      <div>
                        <p className="upp-file-name">{cert.title}</p>
                        <p className="upp-file-meta">
                          {[cert.issuer, cert.issueDate].filter(Boolean).join(' · ')}
                        </p>
                        {cert.description && <p className="upp-file-meta">{cert.description}</p>}
                        {cert.issuedBy === 'organizer' && (
                          <div className="upp-cert-review">
                            {(cert.organizationName || cert.programTitle) && (
                              <p className="upp-file-meta upp-cert-review-context">
                                Issued by {[cert.organizationName, cert.programTitle].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            {cert.rating != null && (
                              <div className="upp-cert-review-rating">
                                <StarRow rating={cert.rating} />
                                <span className="upp-cert-review-rating-num">{cert.rating}/5</span>
                              </div>
                            )}
                            {cert.reviewComment && (
                              <p className="upp-cert-review-quote">&ldquo;{cert.reviewComment}&rdquo;</p>
                            )}
                          </div>
                        )}
                      </div>
                      {cert.fileUrl && (
                        <div className="upp-file-actions">
                          <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer" className="upp-file-link">{t('userPublicProfile.viewPdf')}</a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* REVIEWS (organizations only) */}
          {isOrganization && activeTab === 'reviews' && (
            <div className="upp-files-section upp-reviews-section">
              <h3 className="upp-section-title">Reviews from participants</h3>
              {!profile.orgRatingCount || profile.orgRatingAvg == null ? (
                <div className="upp-empty"><Star size={40} className="upp-empty-icon" /><p>No reviews yet</p></div>
              ) : (
                <>
                  <div className="upp-reviews-summary">
                    <span className="upp-reviews-summary-num">{Number(profile.orgRatingAvg).toFixed(1)} ★</span>
                    <span className="upp-reviews-summary-count">based on {profile.orgRatingCount} review{profile.orgRatingCount === 1 ? '' : 's'}</span>
                  </div>
                  <div className="upp-reviews-list">
                    {(profile.orgReviews || []).map((review) => (
                      <div key={review.id} className="upp-review-item">
                        <img
                          src={getUserAvatarSrc(review.user)}
                          alt={review.user?.name || review.user?.username || ''}
                          className="upp-review-avatar"
                          onError={(e) => { e.target.src = defaultAvatar; }}
                        />
                        <div className="upp-review-body">
                          <div className="upp-review-header">
                            <span className="upp-review-name">{review.user?.name || review.user?.username || 'Anonymous'}</span>
                            <span className="upp-review-time">{formatDate(review.createdAt, t)}</span>
                          </div>
                          <div className="upp-cert-review-rating">
                            <StarRow rating={review.rating} />
                            <span className="upp-cert-review-rating-num">{review.rating}/5</span>
                          </div>
                          {review.comment && <p className="upp-review-comment">{review.comment}</p>}
                          {review.programTitle && <p className="upp-file-meta">{review.programTitle}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* FOLLOWERS */}
          {activeTab === 'followers' && (
            <div className="upp-users-section">
              {followers.length === 0 ? (
                <div className="upp-empty"><Users size={40} className="upp-empty-icon" /><p>{t('userPublicProfile.noFollowersYet')}</p></div>
              ) : (
                followers.map((follower) => {
                  const u = follower.user || follower.follower || follower;
                  const isFol = following.some(f => (f._id || f) === u._id);
                  return (
                    <div key={u._id} className="upp-user-card">
                      <div className="upp-user-left" onClick={() => navigateToProfile(u._id)}>
                        <img src={getUserAvatarSrc(u)} alt={u.name} className="upp-user-avatar" onError={(e) => { e.target.src = defaultAvatar; }} />
                        <div>
                          <p className="upp-user-name">{u.organizationName || u.name}</p>
                          <p className="upp-user-handle">@{u.username || u.email?.split('@')[0]}</p>
                          <div className="upp-user-badges">
                            {u.userType === 'organizer' && <span className="upp-badge upp-badge-type">{t('userPublicProfile.orgBadge')}</span>}
                            {isFol && <span className="upp-badge upp-badge-mutual">{t('userPublicProfile.followsYou')}</span>}
                          </div>
                        </div>
                      </div>
                      {u._id !== currentUser?._id && (
                        isFol ? (
                          <span className="upp-badge upp-badge-friends"><UserCheck size={12} /> {t('userPublicProfile.friends')}</span>
                        ) : (
                          <FollowButton userId={u._id} username={u.organizationName || u.name} profileImage={u.profileImage} onFollowChange={handleFollowStatusChange} />
                        )
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* FOLLOWING */}
          {activeTab === 'following' && (
            <div className="upp-users-section">
              {following.length === 0 ? (
                <div className="upp-empty"><Users size={40} className="upp-empty-icon" /><p>{t('userPublicProfile.notFollowingAnyone')}</p></div>
              ) : (
                following.map((fu) => {
                  const u = fu.user || fu.followedUser || fu;
                  const isFolower = followers.some(f => (f._id || f) === u._id);
                  return (
                    <div key={u._id} className="upp-user-card">
                      <div className="upp-user-left" onClick={() => navigateToProfile(u._id)}>
                        <img src={getUserAvatarSrc(u)} alt={u.name} className="upp-user-avatar" onError={(e) => { e.target.src = defaultAvatar; }} />
                        <div>
                          <p className="upp-user-name">{u.organizationName || u.name}</p>
                          <p className="upp-user-handle">@{u.username || u.email?.split('@')[0]}</p>
                          <div className="upp-user-badges">
                            {u.userType === 'organizer' && <span className="upp-badge upp-badge-type">{t('userPublicProfile.orgBadge')}</span>}
                            {isFolower && <span className="upp-badge upp-badge-mutual">{t('userPublicProfile.mutual')}</span>}
                          </div>
                        </div>
                      </div>
                      {u._id !== currentUser?._id && (
                        isFolower ? (
                          <span className="upp-badge upp-badge-friends"><UserCheck size={12} /> {t('userPublicProfile.friends')}</span>
                        ) : (
                          <FollowButton userId={u._id} username={u.organizationName || u.name} profileImage={u.profileImage} onFollowChange={handleFollowStatusChange} />
                        )
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {previewFile && (
          <div className="upp-preview-overlay" onClick={() => setPreviewFile(null)}>
            <div className="upp-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="upp-preview-header">
                <h3>{previewFile.name}</h3>
                <button type="button" onClick={() => setPreviewFile(null)} className="upp-preview-close">
                  <X size={20} />
                </button>
              </div>
              <div className="upp-preview-body">
                  {getFileKind(previewFile) === 'pdf' ? (
                    <iframe src={previewFile.resolvedUrl} title={previewFile.name} className="upp-preview-frame" />
                  ) : getFileKind(previewFile) === 'image' ? (
                    <img src={previewFile.resolvedUrl} alt={previewFile.name} className="upp-preview-image" />
                  ) : (
                    <div className="upp-empty"><p>{t('userPublicProfile.previewNotAvailable')}</p></div>
                  )}
              </div>
              <div className="upp-preview-footer">
                <button type="button" onClick={() => handleDownloadFile(previewFile)} className="upp-file-link">{t('userPublicProfile.download')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Report User Modal */}
        {showReportModal && (
          <div className="upp-modal-overlay" onClick={() => setShowReportModal(false)}>
            <div className="upp-modal upp-report-modal" onClick={(e) => e.stopPropagation()}>
              <button className="upp-modal-close" onClick={() => setShowReportModal(false)}>
                <X size={20} />
              </button>
              <h3 className="upp-report-title">
                <Flag size={18} color="var(--upp-danger)" /> {t('userPublicProfile.reportModalTitle', { name: displayName })}
              </h3>
              <p className="upp-report-subtitle">{t('userPublicProfile.reportModalSubtitle')}</p>
              <div className="upp-report-reasons">
                {REPORT_REASONS.map((r) => (
                  <label key={r.id} className={`upp-report-reason${reportReasonId === r.id ? ' is-selected' : ''}`}>
                    <input type="radio" name="reportReason" checked={reportReasonId === r.id} onChange={() => setReportReasonId(r.id)} />
                    {r.label}
                  </label>
                ))}
              </div>
              <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder={t('userPublicProfile.detailsPlaceholder')} rows={3}
                className="upp-report-textarea" />
              <div className="upp-report-actions">
                <button onClick={() => setShowReportModal(false)} className="upp-report-cancel">{t('userPublicProfile.cancel')}</button>
                <button onClick={handleSubmitReport} disabled={!reportReasonId || reporting} className="upp-report-submit">
                  {reporting ? t('userPublicProfile.submitting') : t('userPublicProfile.submitReport')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post Modal */}
        {showPostModal && selectedPost && (
          <div className="upp-modal-overlay" onClick={() => setShowPostModal(false)}>
            <div className="upp-modal" onClick={e => e.stopPropagation()}>
              <button className="upp-modal-close" onClick={() => setShowPostModal(false)}>
                <X size={20} />
              </button>
              <div className="upp-modal-body">
                <div className="upp-modal-media">
                  {selectedPost.images && selectedPost.images.length > 0 ? (
                    <ImageGallery images={selectedPost.images} getImageUrl={getImageUrl} onImageClick={() => {}} />
                  ) : (
                    <div className="upp-modal-text-post">
                      <p>{selectedPost.content}</p>
                    </div>
                  )}
                </div>
                <div className="upp-modal-sidebar">
                  <div className="upp-modal-author">
                    <img src={getAvatarSrc()} alt={displayName} className="upp-modal-avatar" onError={(e) => { e.target.src = defaultAvatar; }} />
                    <div>
                      <p className="upp-modal-name">{displayName}</p>
                      <p className="upp-modal-time">{formatDate(selectedPost.createdAt, t)}</p>
                    </div>
                  </div>
                  {selectedPost.content && selectedPost.images?.length > 0 && (
                    <p className="upp-modal-caption">{selectedPost.content}</p>
                  )}
                  <div className="upp-modal-actions">
                    <button className={`upp-modal-action${isPostLiked(selectedPost) ? ' liked' : ''}`} onClick={() => handleLikePost(selectedPost._id)}>
                      <Heart size={18} fill={isPostLiked(selectedPost) ? 'var(--upp-danger)' : 'none'} color={isPostLiked(selectedPost) ? 'var(--upp-danger)' : 'currentColor'} />
                      <span>{selectedPost.likes?.length || 0}</span>
                    </button>
                    <button className="upp-modal-action" onClick={() => commentInputRef.current?.focus()}>
                      <MessageCircle size={18} />
                      <span>{selectedPost.comments?.length || 0}</span>
                    </button>
                    <button className={`upp-modal-action${isPostSaved(selectedPost) ? ' saved' : ''}`} onClick={() => handleSavePost(selectedPost._id)}>
                      <Bookmark size={18} fill={isPostSaved(selectedPost) ? 'var(--green-500)' : 'none'} color={isPostSaved(selectedPost) ? 'var(--green-500)' : 'currentColor'} />
                    </button>
                  </div>
                  <div className="upp-modal-comments">
                    <p className="upp-modal-comments-title">{t('userPublicProfile.comments')}</p>
                    {selectedPost.comments?.filter(c => !c.parentCommentId).length > 0 ? (
                      selectedPost.comments.filter(c => !c.parentCommentId).map(comment => (
                        <div key={comment._id} className="upp-modal-comment">
                          <img src={getImageUrl(comment.author?.profileImage)} alt="" className="upp-modal-comment-av" onError={(e) => { e.target.src = defaultAvatar; }} />
                          <div>
                            <span className="upp-modal-comment-name">{comment.author?.name}</span>
                            <span className="upp-modal-comment-time"> · {formatDate(comment.createdAt, t)}</span>
                            <p className="upp-modal-comment-text">{comment.content}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="upp-modal-no-comments">{t('userPublicProfile.noCommentsYet')}</p>
                    )}
                  </div>
                  <div className="upp-modal-add-comment">
                    <input
                      ref={commentInputRef}
                      type="text"
                      placeholder={t('userPublicProfile.addCommentPlaceholder')}
                      value={commentInputs[selectedPost._id] || ''}
                      onChange={e => setCommentInputs({ ...commentInputs, [selectedPost._id]: e.target.value })}
                      onKeyPress={e => e.key === 'Enter' && handleComment(selectedPost._id)}
                    />
                    <button onClick={() => handleComment(selectedPost._id)} disabled={!commentInputs[selectedPost._id]?.trim()}>
                      <Send size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default UserPublicProfile;
